import { getEnv } from "../config/env";

// Social sign-in via Supabase GoTrue, without supabase-js (ADR-0013). The
// browser is redirected to GoTrue's `/authorize` endpoint; on return the
// session tokens arrive in the URL fragment (implicit flow). Sign-in and
// sign-up share one flow — GoTrue creates the account on first use, and the
// `handle_new_user` DB trigger provisions the RecruitTrucker account row.

/** The Supabase project URL. Uses VITE_SUPABASE_URL if set, else derives it
 *  from the functions URL (…/functions/v1). */
export function supabaseUrl(): string {
  const env = getEnv();
  return env.VITE_SUPABASE_URL ?? env.VITE_FUNCTIONS_URL.replace(/\/functions\/v1\/?$/, "");
}

/** GoTrue authorize URL for a provider (implicit flow → tokens in the hash). */
export function providerAuthUrl(provider: string, origin: string): string {
  const redirect = `${origin}/auth/callback`;
  return `${supabaseUrl()}/auth/v1/authorize?provider=${encodeURIComponent(provider)}` +
    `&redirect_to=${encodeURIComponent(redirect)}`;
}

/** Start the Google sign-in / sign-up redirect. */
export function startGoogleSignIn(): void {
  window.location.assign(providerAuthUrl("google", window.location.origin));
}

export interface OAuthTokens {
  access_token: string;
  refresh_token: string;
}

/** Parse the GoTrue OAuth redirect fragment: tokens, an error, or null. */
export function parseOAuthHash(hash: string): OAuthTokens | { error: string } | null {
  const h = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!h) return null;
  const p = new URLSearchParams(h);
  const err = p.get("error_description") ?? p.get("error");
  if (err) return { error: err };
  const access_token = p.get("access_token");
  const refresh_token = p.get("refresh_token");
  if (access_token && refresh_token) return { access_token, refresh_token };
  return null;
}

/** Decode the `sub` (user id) claim from a JWT without verifying it. The token
 *  is verified server-side on every call; this only labels the local session. */
export function jwtSub(token: string): string {
  try {
    const payload = token.split(".")[1];
    if (!payload) return "";
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return (JSON.parse(json).sub as string) ?? "";
  } catch {
    return "";
  }
}
